import type { GroupedSelectOption, SelectOption } from '@scaleaq/scaleui';

import type {
  ApplyValueChangeArgs,
  CreatableSelectOption,
  FlatOption,
  OptionSection,
} from './controlledCreatableMultiSelect.types.ts';

export const optionKey = (value: unknown) => {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return String(value);
  }
};

export const valuesEqual = <V>({ left, right }: { left: V; right: V }) =>
  optionKey(left) === optionKey(right);

export const valuesEqualList = <V>({ left, right }: { left: V[]; right: V[] }) =>
  left.length === right.length &&
  left.every((value, index) => valuesEqual({ left: value, right: right[index] }));

export const getOptionLabel = <V>({ item, options }: { item: V; options: FlatOption<V>[] }) =>
  options.find(entry => valuesEqual({ left: entry.value, right: item }))?.label ?? optionKey(item);

export const matchesOption = <V>({
  createValue,
  item,
  options,
  typed,
}: {
  createValue?: V;
  item: V;
  options: FlatOption<V>[];
  typed: string;
}) => {
  const normalizedTyped = typed.trim().toLowerCase();
  if (!normalizedTyped) return true;
  if (createValue !== undefined && valuesEqual({ left: item, right: createValue })) return true;

  const label = getOptionLabel({ item, options }).toLowerCase();
  return label.includes(normalizedTyped) || normalizedKey(item).includes(normalizedTyped);
};

export const buildComboboxOptions = <V>({
  allowCreate = true,
  createdOptions,
  createdValues,
  getCreatedValue,
  options,
  query,
}: {
  allowCreate?: boolean;
  createdOptions: FlatOption<V>[];
  createdValues: V[];
  getCreatedValue?: (input: string) => V;
  options: Array<CreatableSelectOption<V>>;
  query: string;
}) => {
  const flatOptions = flattenOptions(options);
  const existingOptionKeys = toKeySet(flatOptions.map(option => option.value));
  const knownOptions = collectKnownOptions({
    createdOptions,
    createdValues,
    existingOptionKeys,
    flatOptions,
  });
  const createOption = allowCreate
    ? findCreateOption({ getCreatedValue, knownOptions, query })
    : undefined;
  const sections = groupMatchingOptions({ knownOptions, query });
  const optionValues = knownOptions.map(option => option.value);
  const items = createOption ? [...optionValues, createOption.value] : optionValues;

  return { createOption, existingOptionKeys, items, knownOptions, sections };
};

export const resolveSelectedValues = <V>({
  createdValues,
  fieldValue,
  separateCreatedValues,
}: {
  createdValues: V[];
  fieldValue: unknown;
  separateCreatedValues: boolean;
}) => {
  const fieldValues = toValueArray<V>(fieldValue);
  if (!separateCreatedValues) return fieldValues;
  return mergeSelectedValues({ createdValues, fieldValues });
};

export const applyValueChange = <V>({
  catalogReady = true,
  createOption,
  createdValues,
  currentValue,
  existingOptionKeys,
  knownOptions,
  nextValue,
  onChange,
  onCreate,
  onCreatedValuesChange,
  setCreatedOptions,
}: ApplyValueChangeArgs<V>) => {
  // `existingOptionKeys` is only the options currently passed in. That list is empty while the
  // catalog is loading, and a change in that window must not rewrite stored links.
  if (!catalogReady) return;

  const nextSelected = toValueArray<V>(nextValue);
  const currentValues = toValueArray<V>(currentValue);
  rememberAddedOptions({
    createOption,
    currentValues,
    knownOptions,
    nextSelected,
    onCreate,
    setCreatedOptions,
  });

  if (!onCreatedValuesChange) {
    onChange(nextSelected);
    return;
  }

  const { created, known } = splitSelectedValues({
    createOption,
    createdValues,
    currentValues,
    existingOptionKeys,
    nextSelected,
  });
  if (!valuesEqualList({ left: known, right: currentValues })) {
    onChange(known);
  }
  if (!valuesEqualList({ left: created, right: createdValues })) {
    onCreatedValuesChange(created);
  }
};

const normalizedKey = (value: unknown) => optionKey(value).toLowerCase();

const toKeySet = <V>(values: readonly V[]) => new Set(values.map(value => normalizedKey(value)));

const isGroupedOption = <V>(
  option: CreatableSelectOption<V>
): option is GroupedSelectOption<SelectOption<V>> =>
  'options' in option && Array.isArray(option.options);

const flattenOptions = <V>(options: Array<CreatableSelectOption<V>>) => {
  const flatOptions: FlatOption<V>[] = [];
  for (const entry of options) {
    if (!isGroupedOption(entry)) {
      flatOptions.push({ disabled: entry.disabled, label: entry.label, value: entry.value });
      continue;
    }

    for (const option of entry.options) {
      flatOptions.push({
        disabled: option.disabled,
        group: entry.label || undefined,
        label: option.label,
        value: option.value,
      });
    }
  }
  return flatOptions;
};

const pushUniqueOption = <V>({
  knownOptions,
  knownValues,
  option,
}: {
  knownOptions: FlatOption<V>[];
  knownValues: Set<string>;
  option: FlatOption<V>;
}) => {
  const key = normalizedKey(option.value);
  if (knownValues.has(key)) return;
  knownValues.add(key);
  knownOptions.push(option);
};

const collectKnownOptions = <V>({
  createdOptions,
  createdValues,
  existingOptionKeys,
  flatOptions,
}: {
  createdOptions: FlatOption<V>[];
  createdValues: V[];
  existingOptionKeys: Set<string>;
  flatOptions: FlatOption<V>[];
}) => {
  const knownValues = new Set(existingOptionKeys);
  const knownOptions = [...flatOptions];
  for (const option of createdOptions) {
    pushUniqueOption({ knownOptions, knownValues, option });
  }
  for (const value of createdValues) {
    pushUniqueOption({
      knownOptions,
      knownValues,
      option: { label: optionKey(value), value },
    });
  }
  return knownOptions;
};

const findCreateOption = <V>({
  getCreatedValue,
  knownOptions,
  query,
}: {
  getCreatedValue?: (input: string) => V;
  knownOptions: FlatOption<V>[];
  query: string;
}) => {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return;

  const normalizedQuery = trimmedQuery.toLowerCase();
  const queryMatchesOption = knownOptions.some(
    option =>
      normalizedKey(option.value) === normalizedQuery ||
      option.label.toLowerCase() === normalizedQuery
  );
  if (queryMatchesOption) return;

  return {
    label: trimmedQuery,
    value: getCreatedValue ? getCreatedValue(trimmedQuery) : (trimmedQuery as V),
  };
};

const groupMatchingOptions = <V>({
  knownOptions,
  query,
}: {
  knownOptions: FlatOption<V>[];
  query: string;
}) => {
  const visibleOptions = knownOptions.filter(option =>
    matchesOption({
      item: option.value,
      options: knownOptions,
      typed: query,
    })
  );
  const sections: OptionSection<V>[] = [];
  for (const option of visibleOptions) {
    const current = sections[sections.length - 1];
    if (current && current.label === option.group) {
      current.options.push(option);
      continue;
    }

    sections.push({ label: option.group, options: [option] });
  }
  return sections;
};

const mergeSelectedValues = <V>({
  createdValues,
  fieldValues,
}: {
  createdValues: V[];
  fieldValues: V[];
}) => {
  const merged = [...fieldValues];
  const seen = toKeySet(fieldValues);
  for (const value of createdValues) {
    const key = normalizedKey(value);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(value);
  }
  return merged;
};

const toValueArray = <V>(value: unknown) => {
  if (!Array.isArray(value)) return [];
  return value as V[];
};

const labelForAddedValue = <V>({
  createOption,
  value,
}: {
  createOption?: FlatOption<V>;
  value: V;
}) => {
  if (createOption && valuesEqual({ left: value, right: createOption.value })) {
    return createOption.label;
  }
  return optionKey(value);
};

const rememberAddedOptions = <V>({
  createOption,
  currentValues,
  knownOptions,
  nextSelected,
  onCreate,
  setCreatedOptions,
}: {
  createOption?: FlatOption<V>;
  currentValues: V[];
  knownOptions: FlatOption<V>[];
  nextSelected: V[];
  onCreate?: (value: V) => void;
  setCreatedOptions: (update: (current: FlatOption<V>[]) => FlatOption<V>[]) => void;
}) => {
  if (!createOption) return;

  const knownValueKeys = toKeySet([...knownOptions.map(option => option.value), ...currentValues]);
  const added = nextSelected.filter(
    value =>
      valuesEqual({ left: value, right: createOption.value }) &&
      !knownValueKeys.has(normalizedKey(value))
  );
  if (added.length === 0) return;

  setCreatedOptions(current => [
    ...current,
    ...added.map(value => ({
      label: labelForAddedValue({ createOption, value }),
      value,
    })),
  ]);
  for (const value of added) {
    onCreate?.(value);
  }
};

const isCreatedSelection = <V>({
  createOption,
  createdKeys,
  key,
  value,
}: {
  createOption?: FlatOption<V>;
  createdKeys: Set<string>;
  key: string;
  value: V;
}) => {
  if (createOption && valuesEqual({ left: value, right: createOption.value })) return true;
  return createdKeys.has(key);
};

const splitSelectedValues = <V>({
  createOption,
  createdValues,
  currentValues,
  existingOptionKeys,
  nextSelected,
}: {
  createOption?: FlatOption<V>;
  createdValues: V[];
  currentValues: V[];
  existingOptionKeys: Set<string>;
  nextSelected: V[];
}) => {
  const currentKeys = toKeySet(currentValues);
  const createdKeys = toKeySet(createdValues);
  const known: V[] = [];
  const created: V[] = [];
  for (const value of nextSelected) {
    const key = normalizedKey(value);
    // A value is a new name only when the user just created it, or it is already in the
    // created list. Every other selection stays a linked record, including while options are empty.
    if (existingOptionKeys.has(key) || currentKeys.has(key)) {
      known.push(value);
      continue;
    }
    if (isCreatedSelection({ createOption, createdKeys, key, value })) {
      created.push(value);
      continue;
    }
    known.push(value);
  }
  return { created, known };
};
